import BottomSheet, { BottomSheetBackdrop, BottomSheetView } from '@gorhom/bottom-sheet';
import * as Clipboard from 'expo-clipboard';
import * as Haptics from 'expo-haptics';
import {
  Copy,
  MoreHorizontal,
  Plus,
  QrCode,
  Users,
} from 'lucide-react-native';
import { type ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  Share,
  StyleSheet,
  View,
} from 'react-native';

import { colors, radii, spacing } from '@/constants/theme';
import { InstagramIcon } from '@/components/icons/InstagramIcon';
import { TikTokIcon } from '@/components/icons/TikTokIcon';
import { buildShareMessage } from '@/lib/shareLinks';
import { referralLinksStore } from '@/stores/referralLinks.store';
import type { Deal } from '@/types/deal';
import {
  REFERRAL_LINK_CHANNEL,
  REFERRAL_LINK_KIND,
  type ReferralLink,
  type ReferralLinkChannel,
} from '@/types/referral';

import { Text } from './Text';

/**
 * Channel-row icons can be either a Lucide icon (monochrome, accepts
 * color + strokeWidth) or a brand-glyph component (Instagram/TikTok,
 * gradient/multicolor — ignores color/strokeWidth props and renders
 * the brand's actual logo). Both expose `size`, so the tile renders
 * them identically at 18px.
 */
type ChannelIconComponent = ComponentType<{
  size?: number;
  color?: string;
  strokeWidth?: number;
}>;

const TRACKED_CHANNELS: readonly {
  channel: ReferralLinkChannel;
  label: string;
  sub: string;
  icon: ChannelIconComponent;
}[] = [
  {
    channel: REFERRAL_LINK_CHANNEL.Instagram,
    label: 'Instagram',
    sub: 'Story, DM, bio, etc.',
    icon: InstagramIcon,
  },
  {
    channel: REFERRAL_LINK_CHANNEL.TikTok,
    label: 'TikTok',
    sub: 'Bio, DM, comments, etc.',
    icon: TikTokIcon,
  },
  {
    channel: REFERRAL_LINK_CHANNEL.Campus,
    label: 'Campus',
    sub: 'Word of mouth',
    icon: Users,
  },
];

export interface ShareMenuProps {
  /** Whether the sheet is currently open. Caller drives via state. */
  isOpen: boolean;
  /** Called whenever the sheet closes (drag, backdrop, or programmatic). */
  onClose: () => void;
  /** Currently-selected deal for message interpolation. Null in All-mode. */
  selectedDeal: Deal | null;
  /**
   * Optional pre-bound link for the inline-confirmation Share Now flow.
   * When set, the sheet still shows channel options for re-routing, but
   * the Copy/Other actions reuse this link instead of the generic ref id.
   */
  preboundLink?: ReferralLink | null;
  /**
   * May-5: invoked when the user taps the QR row. Caller decides which
   * URL to render in `<QrSheet>` (usually the same tracked URL the
   * channel rows would copy). Pass undefined to hide the QR row.
   */
  onShowQr?: () => void;
  /**
   * May-5: invoked after a channel tap copies its tracked link to the
   * clipboard. The parent typically shows a toast like
   * "Instagram link copied". Channel taps no longer open the system
   * share sheet — only "More apps" does.
   */
  onCopied?: (channelLabel: string) => void;
  /**
   * May-5: opens the parent's TrackedLinkCreateSheet for a custom-named
   * group (campaign). Renders as "+ New group" inside this sheet so the
   * Refer screen no longer needs a standalone "+ Create tracked link"
   * button. Pass undefined to hide the row.
   */
  onCreateNewGroup?: () => void;
  testID?: string;
}

/**
 * Channel-tagged share menu. Each branded channel auto-creates (or
 * reuses) a campaign `ReferralLink` anchored to `selectedDeal` and
 * copies its `?r=<token>` URL to the clipboard. Copy and "More apps"
 * use the same per-deal canonical link so the recipient lands on
 * `selectedDeal`'s public landing page with deal-specific OG.
 *
 * `selectedDeal` is required: every outbound URL must be tied to a
 * specific deal. When the parent's deal context isn't ready (initial
 * dealsStore load), the channel/copy rows render but no-op on tap —
 * the parent should generally not open the sheet without a hero deal.
 *
 * Opening behavior is reactive: the parent passes `isOpen`, the sheet
 * imperatively snaps to its open/close state.
 */
export function ShareMenu({
  isOpen,
  onClose,
  selectedDeal,
  preboundLink,
  onShowQr,
  onCopied,
  onCreateNewGroup,
}: ShareMenuProps) {
  const sheetRef = useRef<BottomSheet>(null);
  const [activeChannel, setActiveChannel] = useState<ReferralLinkChannel | null>(null);

  const snapPoints = useMemo(() => ['72%'], []);

  // Drive snap-to-index from the parent's `isOpen` prop.
  useEffect(() => {
    if (!sheetRef.current) return;
    if (isOpen) {
      sheetRef.current.snapToIndex(0);
    } else {
      sheetRef.current.close();
    }
  }, [isOpen]);

  const handleSheetChange = useCallback(
    (index: number) => {
      if (index === -1) onClose();
    },
    [onClose],
  );

  const handleChannelTap = useCallback(
    async (channel: ReferralLinkChannel) => {
      if (activeChannel) return; // ignore taps while a creation is in flight
      // No deal context, or a non-referable deal — parent shouldn't have opened
      // the sheet, but guard so we never mint a $0-referrer link.
      if (!selectedDeal || !selectedDeal.referrals_enabled) return;
      setActiveChannel(channel);
      try {
        const link =
          preboundLink && preboundLink.kind === REFERRAL_LINK_KIND.Campaign && preboundLink.deal_id === selectedDeal.id
            ? preboundLink
            : await referralLinksStore.ensureCampaignLinkForDeal(selectedDeal.id, channel);
        const url = link.public_url;
        // Channel taps copy the tracked link (May-5: dropped the system
        // share sheet here because dismiss-without-pick stuck the spinner
        // and added a paste step the user didn't want). "More apps" still
        // opens Share.share via handleOther.
        await Clipboard.setStringAsync(url);
        if (Platform.OS === 'ios') {
          void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
        }
        onCopied?.(labelForChannel(channel));
        onClose();
      } catch {
        // referralLinksStore.createError captures the failure; surface
        // through the parent's error UI rather than rendering inline here.
      } finally {
        setActiveChannel(null);
      }
    },
    [activeChannel, onClose, onCopied, preboundLink, selectedDeal],
  );

  const resolveDefaultUrl = useCallback(async (): Promise<string | null> => {
    if (preboundLink) return preboundLink.public_url;
    if (!selectedDeal || !selectedDeal.referrals_enabled) return null;
    const link = await referralLinksStore.ensureCampaignLinkForDeal(selectedDeal.id, null);
    return link.public_url;
  }, [preboundLink, selectedDeal]);

  const handleCopy = useCallback(async () => {
    const url = await resolveDefaultUrl();
    if (!url) return;
    await Clipboard.setStringAsync(url);
    if (Platform.OS === 'ios') {
      void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    }
    // The "Copy link" row passes an empty channel label so the parent
    // renders "Link copied" rather than "Copy link link copied".
    onCopied?.('');
    onClose();
  }, [onClose, onCopied, resolveDefaultUrl]);

  const handleOther = useCallback(async () => {
    const url = await resolveDefaultUrl();
    if (!url) return;
    const message = buildShareMessage(selectedDeal);
    // `Share.share` only resolves on iOS once the user acts on the native
    // panel. Calling onClose synchronously after `await` would dismount the
    // sheet while the panel is still animating, so wait until it resolves
    // (or rejects) before closing — same shape the channel-tap path uses.
    try {
      await Share.share({ message: `${message} ${url}`, url });
    } catch (error) {
      // Web Share is absent on most desktop browsers, so `Share.share`
      // rejects there instead of opening a panel. Fall back to copying the
      // same tracked link the "Copy link" row uses so the button still does
      // something. Skip the copy when the user cancelled (AbortError) or
      // double-invoked (InvalidStateError) — those are deliberate no-ops.
      const name = error instanceof Error ? error.name : '';
      if (name !== 'AbortError' && name !== 'InvalidStateError') {
        await Clipboard.setStringAsync(url);
        onCopied?.('');
      }
    } finally {
      onClose();
    }
  }, [onClose, onCopied, resolveDefaultUrl, selectedDeal]);

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} appearsOnIndex={0} disappearsOnIndex={-1} />
    ),
    [],
  );

  return (
    <BottomSheet
      ref={sheetRef}
      index={isOpen ? 0 : -1}
      snapPoints={snapPoints}
      enablePanDownToClose
      backdropComponent={renderBackdrop}
      onChange={handleSheetChange}
      handleIndicatorStyle={styles.handle}
      backgroundStyle={styles.background}
    >
      <BottomSheetView
        style={styles.content}
        accessibilityRole="menu"
        // The sheet stays mounted at index -1 while closed, so without this
        // VoiceOver can land on "Share via Instagram" from a screen where
        // nothing of the sort is visible.
        accessibilityElementsHidden={!isOpen}
        importantForAccessibility={isOpen ? 'auto' : 'no-hide-descendants'}
      >
        <Text variant="bodyMedium" color="deepGreen" style={styles.heading}>
          Pick the link that matches where you&apos;re about to share. We&apos;ll show you which channel each sign-up came from so you can double down on what&apos;s working.
        </Text>
        <View style={styles.options}>
          {/* Without a deal selected (initial load, no opted-in deals)
              every action would silently no-op. Disable up-front so the
              user understands why nothing happens. */}
          {TRACKED_CHANNELS.map((opt) => {
            const isLoading = activeChannel === opt.channel;
            const isDisabled = !selectedDeal || (activeChannel !== null && !isLoading);
            return (
              <ChannelOption
                key={opt.channel}
                icon={opt.icon}
                label={opt.label}
                sub={opt.sub}
                onPress={() => void handleChannelTap(opt.channel)}
                loading={isLoading}
                disabled={isDisabled}
                accessibilityLabel={`Share via ${opt.label}`}
                testID={`share-channel-${opt.channel}`}
              />
            );
          })}
          <View style={styles.divider} />
          <ChannelOption
            icon={Copy}
            label="Copy link"
            sub="Paste anywhere"
            onPress={() => void handleCopy()}
            disabled={!selectedDeal && !preboundLink || activeChannel !== null}
            accessibilityLabel="Copy link to clipboard"
            testID="share-copy"
          />
          {onShowQr ? (
            <ChannelOption
              icon={QrCode}
              label="QR code"
              sub="Have your friend scan it"
              onPress={onShowQr}
              disabled={!selectedDeal || activeChannel !== null}
              accessibilityLabel="Show QR code"
              testID="share-qr"
            />
          ) : null}
          <View style={styles.divider} />
          <ChannelOption
            icon={MoreHorizontal}
            label="More apps"
            sub="Messages, email, anywhere"
            onPress={() => void handleOther()}
            disabled={!selectedDeal && !preboundLink || activeChannel !== null}
            accessibilityLabel="Share via more apps"
            testID="share-other"
          />
          {onCreateNewGroup ? (
            <ChannelOption
              icon={Plus}
              label="Trackable link"
              sub="Track a Group or Person"
              onPress={onCreateNewGroup}
              disabled={activeChannel !== null}
              accessibilityLabel="Create a trackable link"
              testID="share-new-group"
            />
          ) : null}
        </View>
      </BottomSheetView>
    </BottomSheet>
  );
}

function labelForChannel(channel: ReferralLinkChannel): string {
  switch (channel) {
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'campus':
      return 'Campus';
    case 'text':
      return 'Text';
    case 'email':
      return 'Email';
    case 'copy':
      return 'Copy';
    default:
      return 'Other';
  }
}

interface ChannelOptionProps {
  icon: ChannelIconComponent;
  label: string;
  sub: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
  accessibilityLabel: string;
  testID?: string;
}

function ChannelOption({
  icon: Icon,
  label,
  sub,
  onPress,
  loading,
  disabled,
  accessibilityLabel,
  testID,
}: ChannelOptionProps) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: !!disabled, busy: !!loading }}
      disabled={disabled}
      style={({ pressed }) => [
        styles.row,
        pressed && !disabled && styles.rowPressed,
        disabled && styles.rowDisabled,
      ]}
      testID={testID}
    >
      <View style={styles.iconTile}>
        <Icon size={18} color={colors.deepGreen} strokeWidth={2} />
      </View>
      <View style={styles.rowText}>
        <Text variant="bodyBold" color="deepGreen" numberOfLines={1}>
          {label}
        </Text>
        <Text variant="caption" color="textMuted" numberOfLines={1}>
          {sub}
        </Text>
      </View>
      {loading && <ActivityIndicator size="small" color={colors.deepGreen} />}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  background: {
    backgroundColor: colors.background,
    borderTopLeftRadius: radii.xl,
    borderTopRightRadius: radii.xl,
  },
  handle: {
    backgroundColor: colors.border,
    width: 36,
  },
  content: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing['2xl'],
    gap: spacing.lg,
  },
  heading: {
    paddingHorizontal: spacing.xs,
  },
  options: {
    gap: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: 12,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.base,
    minHeight: 56,
  },
  rowPressed: {
    backgroundColor: 'rgba(27, 64, 48, 0.06)',
  },
  rowDisabled: {
    opacity: 0.5,
  },
  iconTile: {
    width: 44,
    height: 44,
    borderRadius: radii.base,
    backgroundColor: 'rgba(248, 252, 249, 1)',
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border,
    marginVertical: spacing.xs,
    marginHorizontal: spacing.sm,
  },
});

